import { useEffect, useState } from "react";
import classNames from "classnames";
import { Link, useLocation } from "react-router-dom";
import FreestarAdSlot from "@freestar/pubfig-adslot-react-component";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import { FREESTAR_PUBLISHER, FreestarPlacements } from "./freestar-placements";

const PLUS_UTM = "utm_source=uggbuilds";
const PLUS_EXTERNAL_URL = `https://u.gg/plus?${PLUS_UTM}`;

interface FreestarInContentContainerProps {
  className?: string;
  id?: string;
  unitId?: string;
  placement: FreestarPlacements;
  header?: string;
  showAdLabel?: boolean;
  showPlusLink?: boolean;
  slotClassName?: string;
  inNewWindow?: boolean;
  ignoreSkinAd?: boolean;
}

export const FreestarInContentContainer = ({
  className,
  id,
  unitId,
  placement,
  header,
  showAdLabel,
  showPlusLink,
  slotClassName,
  inNewWindow,
}: FreestarInContentContainerProps) => {
  const hasChrome = !!showAdLabel || !!showPlusLink;

  return (
    <div
      className={classNames(
        "flex flex-col rounded-[3px]",
        hasChrome ? "bg-purple-200 overflow-hidden" : "bg-[#191937] flex-column gap-y-[6px]",
        {
          "px-[18px] pb-[18px] pt-[12px]": !!header && !hasChrome,
          "p-[18px]": !header && !hasChrome,
        },
        className,
      )}
    >
      {hasChrome && (
        <div className="flex items-center justify-between px-[12px] py-[6px] text-[10px] leading-none bg-purple-400">
          {showAdLabel && <span className="text-lavender-50">Advertisement</span>}
          {showPlusLink && (
            <Link
              to={`${AppRoutes.PLUS}?${PLUS_UTM}`}
              onClick={
                inNewWindow
                  ? (e) => {
                      e.preventDefault();
                      window.open(PLUS_EXTERNAL_URL);
                    }
                  : undefined
              }
              className="ml-auto text-accent-yellow-400"
            >
              Go Ad-Free
            </Link>
          )}
        </div>
      )}
      {header && !hasChrome && <div className="text-white text-[14px] h-[18px]">{header}</div>}
      <FreestarInContent id={id} unitId={unitId} placement={placement} slotClassName={slotClassName} />
    </div>
  );
};

interface FreestarSlotProps {
  placement: FreestarPlacements;
  slotId: string;
  // Out-of-page units should survive filter changes; in-page units should not.
  refreshOnSearch?: boolean;
}

export function useSettledAdUrl(refreshOnSearch = true) {
  const { pathname, search } = useLocation();
  const url = refreshOnSearch ? `${pathname}${search}` : pathname;
  const [settledUrl, setSettledUrl] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setSettledUrl(url));
    return () => clearTimeout(timer);
  }, [url]);

  return settledUrl;
}

export function FreestarSlot({ placement, slotId, refreshOnSearch = true }: FreestarSlotProps) {
  const settledUrl = useSettledAdUrl(refreshOnSearch);

  if (!settledUrl) return null;

  return <FreestarAdSlot key={settledUrl} publisher={FREESTAR_PUBLISHER} placementName={placement} slotId={slotId} />;
}

interface FreestarInContentProps {
  id?: string;
  unitId?: string;
  placement: FreestarPlacements;
  slotClassName?: string;
}

const FreestarInContent = ({
  id = "ugg-in-content",
  unitId = "fs-in-content",
  placement,
  slotClassName = "h-[300px] w-full",
}: FreestarInContentProps) => (
  <div id={id} className="flex flex-col items-center w-full">
    <div className={slotClassName}>
      <FreestarSlot placement={placement} slotId={unitId} />
    </div>
  </div>
);
